import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  BioinformaticReport,
  BioinformaticReportQueriesFindOneArgs,
  Query
} from '@interfaces/schema';

/**
 * Report View Trait Result Service.
 *
 * @exports
 * @class ReportViewTraitResultService
 */
@Injectable()
export class ReportViewTraitResultService {
  private readonly apollo = inject(Apollo);

  /**
   * Report query.
   *
   * @param {BioinformaticReportQueriesFindOneArgs} [variables] Variables.
   * @returns {Observable<BioinformaticReport>} Observable emitting a BioinformaticReport.
   * @memberof ReportViewTraitResultService
   */
  report(
    variables?: BioinformaticReportQueriesFindOneArgs
  ): Observable<BioinformaticReport> {
    const query = gql`
      query Report($id: String!, $reportType: String) {
        Bioinformatic {
          Report {
            findOne(id: $id, reportType: $reportType) {
              id
              value: score
              panel {
                id
                name
                thumbnail
              }
              genotypes {
                gene {
                  id
                  name
                  description
                }
                types
              }
              score: traitScore {
                id
                name: rating
                minScore: from
                maxScore: to
                description: interpretation
                shortInterpretation
                symptoms
                dos
                donts
              }
              trait {
                id
                name
                description
              }
              category {
                id
                name
              }
              dnaTrait {
                id
                name
                description
                guidelineType
              }
              dnaCategory {
                id
                name
                thumbnail
              }
              dnaScoreTypeGuideline {
                id
                mappedScore
                response
                dScoreType {
                  id
                  isHigherBetter
                  dScoreTypeGuideline {
                    id
                    response
                    mappedScore
                    dScoreTypeGuidelineResponse {
                      id
                      parameterBar
                      response
                      language
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({ query, variables })
      .pipe(
        map(
          ({ data }) =>
            data?.Bioinformatic?.Report?.findOne as BioinformaticReport
        )
      );
  }
}
